import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BlogSeo from './BlogSeo';
import BlogNotFound from './BlogNotFound';
import BlogPostIcon from './BlogPostIcon';
import { getBlogPostByLegacyId, getBlogPostBySlug, getSortedBlogPosts } from '../blogs/blogConfig';
import authors from '../blogs/authors.json';
import blogDates from '../blogs/blogDates.json';
import devTemplateTestPost from '../blogs/devTemplateTestPost.json';
import devTemplateTestDates from '../blogs/devTemplateTestDates.json';
import postStats from '../blogs/postStats.json';
import { normalizeArticleHtml } from '../utils/normalizeArticleHtml';
import { getCliqloanSiteUrls } from '../utils/cliqloanSiteUrls';
import { resolveMetaDescription } from '../utils/metaDescription';
import { enrichArticleHtml } from '../utils/blogPostContent';
const devTestEnabled = process.env.REACT_APP_BLOG_TEMPLATE_TEST === 'true';

const blogAssetPath = (assetPath) => {
  const value = String(assetPath || '');
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('/blog/')) return value;
  return `${process.env.PUBLIC_URL || '/blog'}${value.startsWith('/') ? value : `/${value}`}`;
};

const formatDate = (isoDate) => new Intl.DateTimeFormat('en-US', {
  year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
}).format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));

const extractHtmlContent = (html) => {
  const publicRootMatch = html.match(/<div[^>]*id=["']public-root["'][^>]*>([\s\S]*?)<\/div>/i);
  if (publicRootMatch) return publicRootMatch[1];
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch ? bodyMatch[1] : html;
};

const recommendedPosts = (currentPost) => {
  const candidates = getSortedBlogPosts().filter((post) => post.slug !== currentPost.slug);
  return [
    ...candidates.filter((post) => post.category === currentPost.category),
    ...candidates.filter((post) => post.category !== currentPost.category),
  ].slice(0, 3);
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const legacyId = /^\d+$/.test(slug || '') ? parseInt(slug, 10) : null;
  const standardPost = legacyId ? getBlogPostByLegacyId(legacyId) : getBlogPostBySlug(slug);
  const isTestPost = devTestEnabled && slug === devTemplateTestPost.slug;
  const blog = standardPost || (isTestPost ? devTemplateTestPost : null);
  const dates = blog ? (isTestPost ? devTemplateTestDates : blogDates[blog.slug]) : null;
  const author = blog ? authors[blog.authorId] : null;
  const [article, setArticle] = useState({ html: '', toc: [], words: 0, readMinutes: 1 });
  const [activeSection, setActiveSection] = useState('');
  const [progress, setProgress] = useState(0);
  const [copyLabel, setCopyLabel] = useState('Share link');
  const siteUrls = useMemo(() => getCliqloanSiteUrls(), []);
  const recommendations = useMemo(() => blog ? recommendedPosts(blog) : [], [blog]);
  const description = blog ? resolveMetaDescription(blog.seoDescription || blog.description, article.html) : '';
  const displayedHero = blog?.preloadHero ? {
    avif: blogAssetPath(blog.image.replace(/\.png$/i, '.avif')),
    png: blogAssetPath(blog.image),
  } : null;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);

  useEffect(() => {
    if (legacyId && blog) navigate(`/blog/${blog.slug}/`, { replace: true });
  }, [legacyId, blog, navigate]);

  useEffect(() => {
    if (!blog) {
      setArticle({ html: '', toc: [], words: 0, readMinutes: 1 });
      return undefined;
    }
    let active = true;
    fetch(`${process.env.PUBLIC_URL}${blog.contentPath}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load content: ${response.status}`);
        return response.text();
      })
      .then((data) => {
        if (!active) return;
        const normalized = normalizeArticleHtml(extractHtmlContent(data), siteUrls, {
          stripTitle: blog.title,
          stripFirstH1: false,
          stripBlogImages: false,
          heroImage: blog.image,
          publicUrlPrefix: process.env.PUBLIC_URL || '/blog',
        });
        setArticle(enrichArticleHtml(normalized));
      })
      .catch((error) => {
        console.error('Error fetching blog content:', error);
        if (active) setArticle({ html: '', toc: [], words: 0, readMinutes: 1 });
      });
    return () => { active = false; };
  }, [blog, siteUrls]);

  useEffect(() => {
    const onScroll = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(height > 0 ? Math.min(100, Math.max(0, (window.scrollY / height) * 100)) : 0);
      let current = '';
      article.toc.forEach(({ id }) => {
        const section = document.getElementById(id);
        if (section && section.getBoundingClientRect().top <= 140) current = id;
      });
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [article.toc]);

  if (!blog) {
    return <><BlogSeo title="Page not found | CliQloan Blog" description="The requested blog article could not be found." canonicalPath="/blog/404" noIndex /><BlogNotFound /></>;
  }

  const canonicalUrl = `https://www.cliqloan.com/blog/${blog.slug}/`;
  const sidebar = blog.sidebarCta || {
    heading: 'See CliQloan on a real loan file',
    text: 'One loan origination system, one shared loan file, from lead to lender submission.',
  };

  const copyCurrentUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLabel('Link copied');
      window.setTimeout(() => setCopyLabel('Share link'), 2000);
    } catch {
      setCopyLabel('Copy unavailable');
    }
  };

  return (
    <>
      <BlogSeo title={blog.seoTitle || `${blog.title} | CliQloan Blog`} ogTitle={blog.ogTitle} description={description} canonicalPath={blog.canonicalPath || `/blog/${blog.slug}`} image={blog.image} imageAlt={blog.imageAlt || blog.title} preloadImage={displayedHero?.avif} publishedTime={dates?.published} modifiedTime={dates?.modified} noIndex={Boolean(blog.noIndex)} />
      <div className="bp-progress" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
      <main className="bp-page">
        <section className="bp-subheader"><div className="bp-wide bp-subheader-inner">
          <Link className="bp-back" to="/blog/"><BlogPostIcon name="arrowLeft" size={18} />Back to Blog</Link>
          <nav aria-label="Breadcrumb"><ol className="bp-breadcrumb"><li><a href="https://www.cliqloan.com/">Home</a></li><li><BlogPostIcon name="chevronRight" size={14} /><Link to="/blog/">Blog</Link></li><li><BlogPostIcon name="chevronRight" size={14} /><span aria-current="page">{blog.breadcrumbTitle || blog.title}</span></li></ol></nav>
        </div></section>

        <section className="bp-masthead"><div className="bp-reading-width">
          <span className="bp-category"><i />{blog.category}</span>
          <h1>{blog.title}</h1>
          <p className="bp-dek">{blog.description}</p>
          <div className="bp-meta-row"><div className="bp-author-summary"><span className="bp-avatar" aria-hidden="true">{author?.initials || 'RY'}</span><div><strong>{author?.name || 'Raman Yakkala'}</strong><p><time dateTime={dates?.published}>Published {formatDate(dates.published)}</time>{dates.modified.slice(0, 10) !== dates.published.slice(0, 10) && <><span> · </span><time dateTime={dates.modified}>Updated {formatDate(dates.modified)}</time></>}<span> · {article.readMinutes || postStats[blog.slug]?.readMinutes || 1} min read</span></p></div></div><div className="bp-actions"><button type="button" onClick={copyCurrentUrl}><BlogPostIcon name="link" size={18} />{copyLabel}</button><a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"><span aria-hidden="true">in</span></a></div></div>
        </div></section>

        {displayedHero && <section className="bp-hero-wrap"><picture className="bp-hero bp-reading-width"><source srcSet={displayedHero.avif} type="image/avif" /><img className="blog-hero-image" src={displayedHero.png} alt={blog.imageAlt || blog.title} width={blog.imageWidth || 1200} height={blog.imageHeight || 630} loading="eager" decoding="async" fetchPriority="high" /></picture></section>}

        <section className="bp-content-wrap"><div className="bp-wide bp-content-grid">
          <aside className="bp-rail" aria-label="Article navigation">
            <div className="bp-rail-card bp-toc-card"><p className="bp-rail-title"><BlogPostIcon name="book" size={20} />Contents</p><nav aria-label="Table of contents"><ol>{article.toc.map((item) => <li key={item.id}><a className={activeSection === item.id ? 'active' : ''} href={`#${item.id}`}>{item.text}</a></li>)}</ol></nav></div>
            <div className="bp-rail-card bp-author-card"><div className="bp-author-heading"><span className="bp-avatar" aria-hidden="true">{author?.initials || 'RY'}</span><div><strong>{author?.name || 'Raman Yakkala'}</strong>{author?.role && <p>{author.role}</p>}</div></div>{author?.bio && <p>{author.bio}</p>}<a href="mailto:support-cliqloan@amitasoft.com"><BlogPostIcon name="mail" size={16} />Get in touch</a></div>
            <div className="bp-rail-card bp-sidebar-cta"><span>Product walkthrough</span><p className="bp-sidebar-title">{sidebar.heading}</p><p>{sidebar.text}</p><a href="https://www.cliqloan.com/schedule-demo">Schedule a demo</a></div>
          </aside>
          <article className="bp-article"><div className="bp-body" dangerouslySetInnerHTML={{ __html: article.html }} /><div className="bp-signoff-card"><div><p className="bp-signoff-title">Thanks for reading</p><p>Let's connect and continue the conversation.</p></div><div><a href="mailto:support-cliqloan@amitasoft.com"><BlogPostIcon name="mail" size={18} />Email Raman</a><a href="tel:+14084303650"><BlogPostIcon name="phone" size={18} />+1-408-430-3650</a></div></div></article>
        </div></section>

        <section className="bp-demo"><div className="bp-reading-width"><span>Live mortgage demonstration</span><div className="bp-demo-title">See CliQloan on a real loan file</div><p>One loan origination system, one shared loan file, from lead to lender submission.</p><nav aria-label="Explore CliQloan"><a href="https://www.cliqloan.com/verification-engine">Document intelligence</a><a href="https://www.cliqloan.com/mortgage-point-of-sale">Mortgage POS</a><a href="https://www.cliqloan.com/mortgage-broker-software">For brokers</a><a className="primary" href="https://www.cliqloan.com/schedule-demo">Schedule a demo</a></nav></div></section>
        <section className="bp-related"><div className="bp-wide"><div className="bp-related-heading"><div><span>Keep exploring</span><div className="bp-related-title">Related insights for loan teams</div></div><Link to="/blog/">View all articles<BlogPostIcon name="arrowRight" size={18} /></Link></div><div className="bp-related-grid">{recommendations.map((post) => <a className="bp-related-card" href={`https://www.cliqloan.com/blog/${post.slug}/`} key={post.slug}><div><span>{post.category}</span><h3>{post.title}</h3><p>{post.description}</p></div><small>{postStats[post.slug]?.readMinutes || 1} min read<BlogPostIcon name="arrowRight" size={18} /></small></a>)}</div></div></section>
      </main>
    </>
  );
};

export default BlogDetail;
